import {Control, ScaleLine, defaults as defaultControls} from 'ol/control';
import React, {useState, useEffect, useRef} from 'react';
import {fromLonLat, transform} from 'ol/proj';
import {useTranslation} from "react-i18next";
import {Fill, Stroke, Style} from 'ol/style';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Polygon from 'ol/geom/Polygon';
import TileLayer from 'ol/layer/Tile';
import {observer} from 'mobx-react';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';
import Overlay from 'ol/Overlay';
import OSM from 'ol/source/OSM';
import View from 'ol/View';
import Map from 'ol/Map';
import 'ol/ol.css';
import {BR_CODE_NOT_IN_USE, ECO_NOT_IN_USE, TWITCH_NOT_IN_USE} from '../search/FilterOther.js';
import {getCityName, getShortSpecName} from '../Trans.js';
import {useGlobalStorage} from '../mobx/GlobalStorage.js';
import {LIST_MAP_MAX_ELEM} from '../ListMap.js';
import {mapStyles} from '../MapStyles.js';
import {TAB_ATLAS} from './TaxCommon.js';
import {brCodeList} from '../Lists.js';
import {dStr, tStr} from '../Util.js';
import {tt_grid} from '../a_grid.js';
import {TT_SPEC} from '../Util.js';
import {COMM_OK} from '../WsR2.js';

function MapObsPopup(props) {
	function closeClick(e) {
		props.proceedDetails(1);
	}

	return (
		<div ref={props.popRef} className="overlay-container" id="obs-popup" onClick={closeClick} style={{backgroundColor: 'white', padding: 8, border: '2px solid #cccccc', borderRadius: 10, boxShadow: '2px 4px #888888'}}>
			{props.num > 1 ? <div className="overlay-text" id="obs-pos">{props.pos + 1 + ' / ' + props.num}</div> : null}
			<div className="overlay-text" id="obs-species" />
			<div className="overlay-text" id="obs-date" />
			<div className="overlay-text" id="obs-address" />
		</div>
	);
}

const StyleTglBtn = observer((props) => {
	const gStorage = useGlobalStorage();
	const leftPos = gStorage.taxons.atlas_map_width - 40;

	return (
		<button 
		    className='ol-custom ol-control' 
		    ref={props.ctrlRef}
		    style={{top: 8, left: leftPos, width: 32, height: 32}} 
		    onClick={props.tglStyle}>
			{'S' + props.style}
		</button>
	);
});

const TaxAtlas = observer(() => {
	const [clickFt, setClickFt] = useState({pos:0, fa:[]});
	const [resizeDone, setResizeDone] = useState(false);
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	const ctrlRef = useRef(null);
	const mapRef = useRef(null);
	const popRef = useRef(null);

	useEffect(() => {
		const atlasLayer = new VectorLayer({source: new VectorSource()});

		const initialMap = new Map({
			controls: defaultControls({rotate: false, zoom: false}).extend([new ScaleLine({units: 'metric'})]),

			layers: [
				new TileLayer({
					source: new OSM(),
				}),
				atlasLayer,
			],
		
			/* Zoomaa Suomen kokoiseksi */
			view: new View({
					center: fromLonLat([27.138596, 65.441896]),
					zoom: 5,
				}),
			moveTolerance: 8
		});

		/* ---------------------------------------------------------- */

		initialMap.on('click', mapClick);

		gStorage.taxons.setAtlasMap(initialMap, atlasLayer);

		/* ---------------------------------------------------------- */

		const h = window.innerHeight - 
			(document.getElementById('appbar') ? document.getElementById('appbar').clientHeight : 48) -
			(document.getElementById('taxonname') ? document.getElementById('taxonname').clientHeight : 56) - 
			(document.getElementById('taxontab') ? document.getElementById('taxontab').clientHeight : 48) -
			(document.getElementById('bottombar') ? document.getElementById('bottombar').clientHeight : 48);

		gStorage.taxons.setAtlasMapSize(window.innerWidth, h);

		window.addEventListener('resize', handleResize);

		return (() => {
			window.removeEventListener('resize', handleResize);
		});
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
//  		console.log('useEffect, currents', mapRef.current, ctrlRef.current, popRef.current);

		if (mapRef.current) {
			gStorage.taxons.atlas_map.setTarget(mapRef.current);

			if (ctrlRef.current) {
				while (gStorage.taxons.atlas_map.getControls().getLength() > 2) {
					gStorage.taxons.atlas_map.removeControl(gStorage.taxons.atlas_map.getControls().getArray()[2]);
				}

				gStorage.taxons.atlas_map.addControl(new Control({element: ctrlRef.current}));
			}

			if (popRef.current) {
				while (gStorage.taxons.atlas_map.getOverlays().getLength() > 0) {
					gStorage.taxons.atlas_map.removeOverlay(gStorage.taxons.atlas_map.getOverlays().getArray()[0]);
				}

				gStorage.taxons.atlas_map.addOverlay(new Overlay({element: popRef.current, id: 'obs'}));
			}
		}
		// eslint-disable-next-line
	}, [mapRef.current, ctrlRef.current, popRef.current, gStorage.taxons.tab]);

	
	useEffect(() => {
		if (!gStorage.taxons.atlas_layer)
			return;

		fillMap();
		// eslint-disable-next-line
	}, [gStorage.taxons.atlas_style, gStorage.taxons.atlas_obs]);

	useEffect(() => {
// 		console.log('useEffect, atlas_data', gStorage.taxons.atlas_layer);

		if (!gStorage.taxons.atlas_layer)
			return;

		if (gStorage.taxons.atlas_map.getOverlayById('obs')) 
			gStorage.taxons.atlas_map.getOverlayById('obs').setPosition(undefined);

		fillMap();

		updateViewport();
		// eslint-disable-next-line
	}, [gStorage.taxons.atlas_data]);

	useEffect(() => {
// 		console.log('useEffect: resizeDone', resizeDone);

		if (!gStorage.taxons.atlas_layer) {
			setResizeDone(false);
			return;
		}

		if (resizeDone === false) {
			updateViewport();
		} else {
			setResizeDone(false);
		}
		// eslint-disable-next-line
	}, [resizeDone]);

	/* Update obs popup */
	useEffect(() => {
		if (!gStorage.taxons.atlas_map) {
// 			console.log('useEffect: clikcFt null map!')
			return;
		}
		
		const ol = gStorage.taxons.atlas_map.getOverlayById('obs')
		if (!ol) {
// 			console.log('useEffect: clikcFt null ol!')
			return;
		}

		ol.setPosition(undefined);

		if (!clickFt.fa.length || clickFt.pos >= clickFt.fa.length)
			return;

		const bri = clickFt.fa[clickFt.pos].get('br_info');
		if (bri) {
			const c = brCodeList.find((c) => bri.br_code === c.id);

			ol.setPosition(clickFt.fa[clickFt.pos].coordinate);

			document.getElementById('obs-species').innerHTML = bri.N + ':' + bri.E + ' ' + taxAtlasGetCityName(bri.city_id) + ' ' + bri.name;
			document.getElementById('obs-date').innerHTML = c.name + ', ' + t(c.short_desc);
			document.getElementById('obs-address').innerHTML = '';
		}

		const obs = clickFt.fa[clickFt.pos].get('obs');
		if (obs) {
			let d_str = '', t_str;

			d_str = dStr(obs.start_t);
			t_str = tStr(obs.start_t);
			if (t_str.length > 0 && t_str !== '0:00')
				d_str += ' ' + t_str;

			ol.setPosition(clickFt.fa[clickFt.pos].coordinate);

			document.getElementById('obs-species').innerHTML = taxAtlasGetSpeciesName(obs.spec_id) + ', ' + obs.num;
			document.getElementById('obs-date').innerHTML = d_str;
			document.getElementById('obs-address').innerHTML = taxAtlasGetCityName(obs.city_id) + ', ' + obs.addr;
		}
		// eslint-disable-next-line
	}, [clickFt]);

	function taxAtlasRxWs(status, msg) {
		if (status !== COMM_OK) {
			console.error('taxAtlasRxWs: error ', status);
			return -1;
		}

		if (msg.cmd === 'tax' && msg.op === 'atlas') {
			if (msg.result === 'OK') {
// 				console.log('taxAtlasRxWs', msg.data);
				const a = msg.data.atlas.filter(gd => gd.br_code >= 40);

				if (a.length > 0) {
					gStorage.taxons.setAtlasData(a);
				}
			} else {
				console.error('taxAtlasRxWs', msg.result);
			}
		} else if (msg.cmd === 'obs' && msg.op === 'list') {
			gStorage.taxons.setAtlasObs(msg.data.obslist);
		} else {
			console.error('taxAtlasRxWs: unknown command', msg.cmd, msg.op, msg.result);
		}
	}

	function loadAtlasData(tax) {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'tax',
			op: 'atlas',
			data: {
				taxon: {
					id: tax.id,
				}
			}
		};

//  		console.log('loadAtlasData', msg);
		gStorage.conn.ws.send(msg, taxAtlasRxWs);
	}

	function loadAtlasObs(tax) {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'obs',
			op: 'list',
			data: {
				count: LIST_MAP_MAX_ELEM,
				pos: 0,
				city: [],
				species: [tax.id],
				trips: [],
				asso: [],
				br_code_mask: BR_CODE_NOT_IN_USE,
				br_code: 0,
				eco: ECO_NOT_IN_USE,
				twitched: TWITCH_NOT_IN_USE,
				tz: 0,
				sort_by: 'time',
				sort_order: 'desc',
			},
		};

//  		console.log('loadAtlasObs', msg);
		gStorage.conn.ws.send(msg, taxAtlasRxWs);
	}
	
	function updateViewport() {
// 		console.log('updateViewport', gStorage.taxons.atlas_layer.getSource().getExtent(), gStorage.taxons.atlas_layer.getSource().getExtent());

		if (gStorage.taxons.atlas_data && gStorage.taxons.atlas_data.length > 0 && 
		    gStorage.taxons.atlas_map && gStorage.taxons.atlas_layer) {
			gStorage.taxons.atlas_map.getView().fit(gStorage.taxons.atlas_layer.getSource().getExtent(), {
				size: gStorage.taxons.atlas_map.getSize(),
				maxZoom: 16,
				padding: [32, 32, 32, 32]});
		} else {
			gStorage.taxons.atlas_map.setView(
				new View({
					center: fromLonLat([27.138596, 65.441896]),
					zoom: 4
				})
			);
		}
	}

	function handleResize() {
		/* Availabilty depends on rendering order => q&d hard coded defaults! */
		const h = window.innerHeight - 
			(document.getElementById('appbar') ? document.getElementById('appbar').clientHeight : 48) -
			(document.getElementById('taxonname') ? document.getElementById('taxonname').clientHeight : 56) - 
			(document.getElementById('taxontab') ? document.getElementById('taxontab').clientHeight : 48) -
			(document.getElementById('bottombar') ? document.getElementById('bottombar').clientHeight : 48);

// 		console.log('handleResize', window.innerWidth, h);
		gStorage.taxons.setAtlasMapSize(window.innerWidth, h);
		setResizeDone(true);
	}

	function fillMap() {
		let features = [];
	
		gStorage.taxons.atlas_data && gStorage.taxons.atlas_data.forEach((g, ind) => {
// 			console.log('fillMap', ind, g.grid_id, g.br_code);
			if (g.br_code >= 40) {
				let c;

				const tgtFeat = new Feature({
					geometry: new Polygon(tt_grid[g.grid_id]),
					br_info: g
				});

				if (g.br_code >= 70) {
					c = 'rgba(0, 255, 0, 0.7)';
				} else if (g.br_code >= 60) {
					c = 'rgba(255, 235, 59, 0.6)';
				} else {
					c = 'rgba(255, 235, 59, 0.3)';
				}

				tgtFeat.setStyle(
					new Style({
						stroke: new Stroke({
							color: 'grey',
							width: 1,
						}),
						fill: new Fill({
							color: c,
						}),
					}),
);
				features.push(tgtFeat);
			}
		});

		gStorage.taxons.atlas_obs && gStorage.taxons.atlas_obs.forEach((obs, i) => {
			const tgtFeat = new Feature({
				geometry: new Point(transform([obs.long_obs / 1000000.0, obs.lat_obs / 1000000.0], 'EPSG:4326', 'EPSG:3857')),
				obs: obs
			});

			tgtFeat.setStyle(mapStyles[gStorage.taxons.atlas_style]);
			features.push(tgtFeat);
		});

		gStorage.taxons.atlas_layer.setSource(
			new VectorSource({
				features: features,
			})
		)
	}

	function mapClick(e) {
		let ft = [], obs = false;

		e.map.forEachFeatureAtPixel(e.pixel, (feat) => {
			if (obs === false) {
				if (feat.get('obs')) {
					obs = true;
				}
			}
		});

		e.map.forEachFeatureAtPixel(e.pixel, (feat) => {
			if (obs === true) {
				if (feat.get('obs')) {
					feat.coordinate = e.coordinate;
					ft.push(feat);
				}
			} else {
				if (!feat.get('obs')) {
					feat.coordinate = e.coordinate;
					ft.push(feat);
				}
			}
		});

		if (obs === true) {
			ft.sort((a, b) => {
				return b.get('obs').start_t - a.get('obs').start_t;
			});
		}

		setClickFt({pos: 0, fa: ft});
	}

	function proceedDetails(dir) {
		setClickFt({pos: clickFt.pos + dir, fa: clickFt.fa});
	}

	function toggleStyle() {
		let tmpStyle = gStorage.taxons.atlas_style;

		if (tmpStyle === 0 && !gStorage.taxons.atlas_obs)
			loadAtlasObs(gStorage.taxons.centerTaxon);

		tmpStyle++;

		if (tmpStyle >= mapStyles.length) 
			tmpStyle = 0;

		gStorage.taxons.setAtlasStyle(tmpStyle);
	}

	function taxAtlasGetCityName(city_id) {
		return getCityName(gStorage.lists.citylist.list.find((city) => city.id === city_id), gStorage.conn.user);
	}

	function taxAtlasGetSpeciesName(spec_id) {
		const spec = gStorage.lists.speclist.list.find((taxon) => taxon.id === spec_id)

		return getShortSpecName(spec, gStorage.conn.user);
	}

	const tax = gStorage.taxons.centerTaxon;

	if (gStorage.taxons.tab !== TAB_ATLAS || !tax || tax.t_type !== TT_SPEC)
		return null;

	if (!gStorage.taxons.atlas_data && gStorage.conn.online)
		loadAtlasData(tax);

	return (<div ref={mapRef} id="mapContainer" style={{width: '100%', height: gStorage.taxons.atlas_map_height}}>
			<StyleTglBtn tglStyle={toggleStyle} style={gStorage.taxons.atlas_style} mapRef={mapRef} ctrlRef={ctrlRef}/>
			<MapObsPopup map={gStorage.taxons.atlas_map} proceedDetails={proceedDetails} pos={clickFt.pos} num={clickFt.fa.length} popRef={popRef}/>
		</div>);
});

export default TaxAtlas;
